import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Loader2, TrendingUp, Banknote, Compass, Briefcase, ArrowRight, FileDown, Share2, Copy, X } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";

/**
 * Hero action bar for the DossierDetail page.
 *
 * Runs the 4 dossier-scoped analyses in parallel:
 *   1. IMES™ diagnostic (POST /diagnostic/dossier/:id/analyze)
 *   2. Bancabilité      (POST /bankability/dossier/:id/compute)
 *   3. Opportunités     (POST /global-opportunities/for-dossier/:id/match)
 *   4. Carrière         (POST /careers/for-dossier/:id/discover)
 *
 * Shows the 4 current scores as clickable tiles so the advisor can jump
 * to each detailed view (either in-page section or sub-page).
 */
export function DossierAnalyseHero({ dossier, onRefresh }) {
  const dossierId = dossier?.dossier_id;
  const [running, setRunning] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [lastError, setLastError] = useState(null);

  const imes = dossier?.economic_data?.diagnostic_imes?.last_score;
  const bank = dossier?.economic_data?.bancabilite?.index;

  const runAll = async () => {
    if (!dossierId) return;
    setRunning(true);
    toast.info("Lancement des 4 moteurs — IMES, Bancabilité, Opportunités, Carrière…");
    const jobs = [
      api.post(`/diagnostic/dossier/${dossierId}/analyze`).catch((e) => ({ __err: "IMES", detail: e?.response?.data?.detail })),
      api.post(`/bankability/dossier/${dossierId}/compute`).catch((e) => ({ __err: "Bancabilité", detail: e?.response?.data?.detail })),
      api.post(`/global-opportunities/for-dossier/${dossierId}/match?top_k=20`).catch((e) => ({ __err: "Opportunités", detail: e?.response?.data?.detail })),
      api.post(`/careers/for-dossier/${dossierId}/discover`).catch((e) => ({ __err: "Carrière", detail: e?.response?.data?.detail })),
    ];
    const results = await Promise.all(jobs);
    const errors = results.filter((r) => r?.__err);
    if (errors.length === 0) toast.success("4 analyses générées avec succès.");
    else if (errors.length < 4) toast.warning(`${4 - errors.length}/4 réussies — échecs : ${errors.map((e) => e.__err).join(", ")}`);
    else toast.error("Aucune analyse n'a pu être générée.");
    setRunning(false);
    onRefresh?.();
  };

  const downloadUnifiedPdf = async () => {
    if (!dossierId) return;
    setExporting(true);
    try {
      const resp = await api.get(`/reports/dossier/${dossierId}/executive-pdf`, { responseType: "blob" });
      // Guard: if backend returned JSON error but with 200 status, blob.type will be application/json
      const ct = resp.data?.type || "";
      if (ct.includes("json") || ct.includes("text")) {
        const txt = await resp.data.text();
        throw new Error(txt || "Erreur inconnue");
      }
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `altiora-bilan-${dossierId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Rapport exécutif téléchargé.");
    } catch (err) {
      // Response body is a Blob (because responseType: blob) even on error — read it back to JSON
      let detail = err?.message;
      let diagnosticId = null;
      const blobData = err?.response?.data;
      if (blobData && typeof blobData.text === "function") {
        try {
          const raw = await blobData.text();
          try {
            const parsed = JSON.parse(raw);
            // Backend now returns {detail: {message, diagnostic_id}}
            if (parsed?.detail && typeof parsed.detail === "object") {
              detail = parsed.detail.message;
              diagnosticId = parsed.detail.diagnostic_id;
            } else {
              detail = parsed?.detail || raw;
            }
          } catch { detail = raw; }
        } catch { /* keep original message */ }
      }
      setLastError({ message: typeof detail === "string" && detail ? detail : "Export PDF indisponible.",
                      diagnosticId });
      toast.error(typeof detail === "string" && detail ? detail : "Export PDF indisponible.");
    } finally { setExporting(false); }
  };

  return (
    <section className="mt-6 mb-8" data-testid="dossier-analyse-hero">
      <div className="rounded-2xl border-2 border-[#0B2545]/25 bg-gradient-to-br from-[#FBF6EA]/60 to-white shadow-sm overflow-hidden">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-[#E4E1D8] px-6 py-4">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">Analyse complète du dossier</div>
            <div className="mt-1 font-display text-lg text-[#0B2545] tracking-tight">
              Lancer les 4 moteurs ALTIORA en une seule fois
            </div>
            <div className="text-xs text-[#5B5B62] mt-0.5">
              IMES™ · Bancabilité · Opportunités mondiales · Découverte carrière
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={runAll}
              disabled={running}
              data-testid="dossier-analyse-run-all"
              className="inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-60 text-white px-5 py-3 text-sm font-medium transition-colors"
            >
              {running ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : <Sparkles className="h-4 w-4" strokeWidth={2} />}
              {running ? "Analyse en cours…" : "Tout analyser"}
            </button>
            <button
              onClick={downloadUnifiedPdf}
              disabled={exporting}
              data-testid="dossier-export-unified-pdf"
              title="Rapport exécutif unifié 7 pages"
              className="inline-flex items-center gap-2 rounded-md border-2 border-[#7A5B14] bg-[#FBF6EA] hover:bg-[#F1E9D0] disabled:opacity-60 text-[#7A5B14] px-4 py-3 text-sm font-medium transition-colors"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : <FileDown className="h-4 w-4" strokeWidth={2} />}
              Bilan PDF
            </button>
            <button
              onClick={() => setShareOpen(true)}
              disabled={!dossierId}
              data-testid="dossier-share-unified-pdf"
              title="Partager le Bilan PDF via un lien signé"
              className="inline-flex items-center gap-2 rounded-md border border-[#0B2545] bg-white hover:bg-[#F1EFE8] disabled:opacity-60 text-[#0B2545] px-4 py-3 text-sm font-medium transition-colors"
            >
              <Share2 className="h-4 w-4" strokeWidth={2} />
              Partager
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 p-4">
          <ScoreTile
            testid="hero-tile-imes"
            icon={TrendingUp}
            label="IMES™"
            hint="Trajectoire éco-sociale"
            value={imes != null ? Number(imes).toFixed(1) : null}
            valueSuffix="/100"
            anchor={`#dossier-imes-section`}
          />
          <ScoreTile
            testid="hero-tile-bank"
            icon={Banknote}
            label="Bancabilité"
            hint="Préparation dossier"
            value={bank != null ? Number(bank).toFixed(1) : null}
            valueSuffix="/100"
            anchor={`#dossier-bankability-section`}
          />
          <ScoreTile
            testid="hero-tile-opps"
            icon={Compass}
            label="Opportunités"
            hint="16 catégories mondiales"
            to={`/app/dossiers/${dossierId}/opportunites`}
            ctaLabel="Voir"
          />
          <ScoreTile
            testid="hero-tile-careers"
            icon={Briefcase}
            label="Carrière"
            hint="7 dimensions cachées"
            to={`/app/dossiers/${dossierId}/carriere`}
            ctaLabel="Voir"
          />
        </div>
      </div>
      {shareOpen && (
        <ShareBilanDialog dossierId={dossierId} onClose={() => setShareOpen(false)} />
      )}
      {lastError && (
        <PdfErrorPanel error={lastError} onDismiss={() => setLastError(null)} />
      )}
    </section>
  );
}


function PdfErrorPanel({ error, onDismiss }) {
  const { has } = usePermissions();
  const isAdmin = has("admin.manage_members") || has("admin.manage_content");
  const [showTrace, setShowTrace] = useState(false);
  const [trace, setTrace] = useState(null);
  const [loadingTrace, setLoadingTrace] = useState(false);

  const loadTrace = async () => {
    if (!error.diagnosticId) return;
    setLoadingTrace(true);
    try {
      const { data } = await api.get(`/reports/admin/pdf-monitoring/failure/${error.diagnosticId}`);
      setTrace(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Trace indisponible.");
    } finally { setLoadingTrace(false); }
  };

  return (
    <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4"
         data-testid="dossier-pdf-error-panel">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 h-5 w-5 rounded-full bg-amber-500 text-white text-[10px] font-mono flex items-center justify-center flex-shrink-0">!</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-amber-900">{error.message}</div>
          {error.diagnosticId && (
            <div className="mt-1 flex items-center gap-3 text-[10px] font-mono text-amber-800">
              <span>diagnostic_id · {error.diagnosticId}</span>
              {isAdmin && (
                <button
                  onClick={() => { setShowTrace(!showTrace); if (!showTrace && !trace) loadTrace(); }}
                  data-testid="dossier-pdf-error-details"
                  className="inline-flex items-center gap-1 uppercase tracking-widest text-amber-900 hover:text-[#0B2545]"
                >
                  {showTrace ? "Masquer" : "Détails (admin)"}
                </button>
              )}
            </div>
          )}
          {showTrace && (
            <div className="mt-3">
              {loadingTrace && <div className="text-xs text-amber-700">Chargement…</div>}
              {trace && (
                <pre className="max-h-64 overflow-auto rounded bg-white/70 border border-amber-200 p-2 text-[10px] font-mono text-[#3A3A3F] whitespace-pre-wrap">
{trace.traceback || "(pas de trace)"}
                </pre>
              )}
            </div>
          )}
        </div>
        <button onClick={onDismiss}
                data-testid="dossier-pdf-error-dismiss"
                className="text-amber-700 hover:text-amber-900 text-xs">×</button>
      </div>
    </div>
  );
}


function ShareBilanDialog({ dossierId, onClose }) {
  const [ttlHours, setTtlHours] = useState(168);
  const [maxDl, setMaxDl] = useState(5);
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/reports/dossier/${dossierId}/unified-share`, {
        ttl_hours: Number(ttlHours) || 168,
        max_downloads: Number(maxDl) || 5,
        recipient_hint: hint.trim() || null,
      });
      const abs = `${window.location.origin}${data.share_url}`;
      setLink({ ...data, absolute: abs });
      toast.success("Lien de partage créé.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible de créer le lien.");
    } finally { setBusy(false); }
  };

  const copy = async () => {
    if (!link?.absolute) return;
    try {
      await navigator.clipboard.writeText(link.absolute);
      toast.success("Lien copié dans le presse-papier.");
    } catch { toast.error("Copie impossible."); }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4"
      onClick={onClose}
      data-testid="dossier-share-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 relative"
      >
        <button
          onClick={onClose}
          data-testid="dossier-share-close"
          className="absolute top-3 right-3 text-[#8B8B8F] hover:text-[#1F2937]"
          aria-label="Fermer"
        ><X className="h-4 w-4" strokeWidth={2} /></button>

        <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
          Partage sécurisé du Bilan
        </div>
        <div className="mt-1 font-display text-xl text-[#0B2545] tracking-tight">
          Envoyer le PDF à un tiers autorisé
        </div>
        <p className="mt-2 text-xs text-[#5B5B62] leading-relaxed">
          Un lien signé, expirable, avec plafond de téléchargements. Il donne accès
          au Bilan PDF <strong>sans exposer votre compte</strong> ni les autres dossiers.
        </p>

        {!link && (
          <div className="mt-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <div className="mb-1 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
                  Durée (heures)
                </div>
                <input
                  type="number" min={1} max={720}
                  value={ttlHours}
                  onChange={(e) => setTtlHours(e.target.value)}
                  data-testid="dossier-share-ttl"
                  className="w-full rounded-md border border-[#E4E1D8] px-3 py-2 text-sm"
                />
              </label>
              <label className="block">
                <div className="mb-1 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
                  Téléchargements max.
                </div>
                <input
                  type="number" min={1} max={500}
                  value={maxDl}
                  onChange={(e) => setMaxDl(e.target.value)}
                  data-testid="dossier-share-maxdl"
                  className="w-full rounded-md border border-[#E4E1D8] px-3 py-2 text-sm"
                />
              </label>
            </div>
            <label className="block">
              <div className="mb-1 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
                Destinataire (indicatif, facultatif)
              </div>
              <input
                type="text"
                placeholder="ex. conseiller@rbc.ca"
                value={hint}
                onChange={(e) => setHint(e.target.value)}
                data-testid="dossier-share-hint"
                className="w-full rounded-md border border-[#E4E1D8] px-3 py-2 text-sm"
              />
            </label>
            <button
              onClick={create}
              disabled={busy}
              data-testid="dossier-share-create"
              className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-60 text-white px-4 py-2.5 text-sm font-medium"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : <Share2 className="h-4 w-4" strokeWidth={2} />}
              Générer le lien
            </button>
          </div>
        )}

        {link && (
          <div className="mt-5 space-y-3" data-testid="dossier-share-result">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14]">
              Lien prêt · expire {new Date(link.expires_at).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" })}
            </div>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={link.absolute}
                data-testid="dossier-share-link-input"
                onFocus={(e) => e.target.select()}
                className="flex-1 rounded-md border border-[#E4E1D8] px-3 py-2 text-xs font-mono bg-[#F8F7F3]"
              />
              <button
                onClick={copy}
                data-testid="dossier-share-copy"
                className="inline-flex items-center gap-1.5 rounded-md bg-[#0B2545] hover:bg-[#123163] text-white px-3 py-2 text-xs"
              >
                <Copy className="h-3.5 w-3.5" strokeWidth={2} /> Copier
              </button>
            </div>
            <div className="text-[10px] font-mono text-[#8B8B8F]">
              Plafond : {link.max_downloads} téléchargement(s). Une nouvelle génération invalide les précédents.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function ScoreTile({ icon: Icon, label, hint, value, valueSuffix, to, anchor, ctaLabel, testid }) {
  const inner = (
    <div className="h-full rounded-lg border border-[#E4E1D8] bg-white hover:border-[#0B2545] hover:shadow-md transition-all px-4 py-3 flex items-center gap-3"
         data-testid={testid}>
      <div className="h-9 w-9 rounded-md bg-[#FBF6EA] flex items-center justify-center flex-shrink-0">
        <Icon className="h-4 w-4 text-[#7A5B14]" strokeWidth={1.8} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">{label}</div>
        {value != null ? (
          <div className="font-mono text-xl text-[#0B2545] tabular-nums leading-tight">
            {value}<span className="text-[10px] opacity-60">{valueSuffix}</span>
          </div>
        ) : (
          <div className="text-xs text-[#5B5B62] italic">{hint}</div>
        )}
      </div>
      <ArrowRight className="h-3.5 w-3.5 text-[#8B8B8F]" strokeWidth={2} />
    </div>
  );
  if (to)     return <Link to={to} className="block">{inner}</Link>;
  if (anchor) return <a href={anchor} className="block">{inner}</a>;
  return inner;
}

export default DossierAnalyseHero;
